<template>
	<div class="search-widget search-widget-global flat-buttons" :class="{ openOptions: opsChain.visible.value }">
		<anchor-element :chain="opsChain" tag="div" class="search-options" @click="toggleDescriptorDrawer" @mousedown.stop>
			<span>{{ env.msg('searchWidget.label') }}</span>
			<sym-icon name="triangle-down-14-full" class="opener" :class="{ closed: !opsChain.visible.value }"></sym-icon>
		</anchor-element>
		<boat-element :chain="opsChain" tag="div" id="searchOptionsBox" class="search-options-box">
			<div><label><input type="checkbox" v-model="selectAll"> <strong>{{ env.msg("qbe.searchAll") }}</strong></label></div>
			<div v-for="d in descriptors" :key="d.id"><label><input type="checkbox" v-model="d.selected"> {{ d.title }}</label></div>
		</boat-element>

		<anchor-element :chain="resultsChain" tag="span" class="search-input">
			<input type="text" v-model="query" class="searchBox" enterkeyhint="search" autocomplete="off" :placeholder="searchPlaceholder"
				   @keydown="keyDown" @focus="inputFocus" @blur="inputBlur" ref="queryInput">
			<sym-icon v-if="query.length > 0" name="cross-circle-14-full" class="empty-search" @click="emptyQuery"></sym-icon>
		</anchor-element>
		<boat-element :chain="resultsChain" tag="div" class="suggestions">
			<div v-for="(r, idx) in results" class="sg-row" :class="{ 'sg-selrow': (idx === selected) }" @mouseenter="selectRow(idx)" @click="doAction(idx)">
				<div class="sg-icon"><sym-icon :name="r.icon"></sym-icon></div>
				<div class="sg-title">{{ r.title }}</div>
			</div>
		</boat-element>
		<button type="button" class="searchButton use-bg" @click="fullSearch"><sym-icon name="search"></sym-icon></button>
	</div>
</template>

<script setup lang="ts">
import {computed, ref, onMounted, watch, nextTick} from "vue";
import SymIcon from "@fd/helpers/SymIcon.vue";
import {injectEnv} from "@fd/Environment";
import type {SearchDescriptor} from "@t/search/Search";
import ClientCache from "@/utils/ClientCache.js";
import type {SearchSuggestItem, SearchSuggestResponse} from "@t/search/Search";
import {getElementMeasure} from "@fd/helpers/DomUtils";
import {go} from "@/router/Router";
import {createChain} from "@/ui/paneltree/PanelTree";
import AnchorElement from "@/ui/paneltree/AnchorElement.vue";
import BoatElement from "@/ui/paneltree/BoatElement.vue";

const env = injectEnv();

const opsChain = createChain({
	visible: false,
	rule: {
		ah: 0, av: 100,
		bh: 0, bv: 0,
		x: 0, y: 0
	}
}, "search-ops");
const resultsChain = createChain({
	visible: false,
	rule: {
		ah: 0, av: 100,
		bh: 0, bv: 0,
		x: 0, y: 0
	}
}, "search-results");

interface SelectableDescriptor extends SearchDescriptor {
	selected: boolean
}

function lastQuery() {
	const q = localStorage.getItem("search.last.query");
	return q == null ? "" : q;
}

function rememberQuery(q:string) {
	if (q) localStorage.setItem("search.last.query", q);
	else localStorage.removeItem("search.last.query");
}

const descriptors = ref<SelectableDescriptor[]>([]);
const query = ref<string>(lastQuery())
const queryInput = ref<HTMLInputElement|null>(null);
const results = ref<SearchSuggestItem[]>([]);
const inFocus = ref<boolean>(false);
const selected = ref<number>(-1);


function emptyQuery() {
	query.value = '';
	if (queryInput.value) queryInput.value.focus();
}

const selectAll = computed({
	get():boolean {
		let sc = 0;
		for (let d of descriptors.value) {
			if (d.selected) sc++;
		}
		return sc === 0 || sc === descriptors.value.length;
	},
	set(v:boolean):void {
		for (let d of descriptors.value) {
			d.selected = false;
		}
		if (!v && descriptors.value.length > 0) {
			descriptors.value[0].selected = true;
		}
	}
});

const checkedSelectors = computed<SelectableDescriptor[]>(() => {
	const ch:SelectableDescriptor[] = [];
	for (let d of descriptors.value) {
		if (d.selected) ch.push(d);
	}
	return ch;
});

watch(checkedSelectors, (checked) => {
	const toSave:string[] = [];
	for (let ch of checked) {
		toSave.push(ch.id);
	}
	localStorage.setItem("_search_scope_", JSON.stringify(toSave));
});

const searchPlaceholder = computed(() => {
	const ch = checkedSelectors.value;
	if (ch.length === 0) return env.msg('searchWidget.searchAll');
	else {
		let ph = env.msg('searchWidget.searchIn');
		const a = env.msg('searchWidget.searchAnd');
		let counter = 0;
		for (let i = 0; i < ch.length; i++) {
			const item = ch[i];
			const separ = counter == 0 ? ' ' : (i == (ch.length - 1) ? ' '+a : ', ');
			if (counter>=3) {
				ph = ph + ', …';
				break;
			}
			ph = ph + separ + item.placeholder;
			counter++;
		}
		return ph;
	}
});

function updateResultsVisibility() {
	resultsChain.visible.value = inFocus.value && !opsChain.visible.value && results.value.length >  0;
}

const rows = computed<number>(() => results.value.length);

function emptyResults():void {
	results.value.length = 0;
	selected.value = -1;
}

function buildRequestEndpoint(query:string, cursorPosition:number, capacity:number):string {
	let endpoint = "searchfeed.do";
	endpoint = env.net.urlParam(endpoint, "q", query);
	if (cursorPosition >= 0) endpoint = env.net.urlParam(endpoint, "cursor", cursorPosition);
	endpoint = env.net.urlParam(endpoint, "capacity", capacity);
	if (selectAll.value) endpoint = env.net.urlParam(endpoint, "searchAll", "on");
	else {
		for (let sel of checkedSelectors.value) {
			endpoint = env.net.urlParam(endpoint, "search("+sel.id+")", "on");
		}
	}
	return endpoint;
}

function buildFullSearchLink(query:string):string {
	let endpoint = "search.do";
	endpoint = env.net.urlParam(endpoint, "query", query);
	if (selectAll.value) endpoint = env.net.urlParam(endpoint, "searchAll", "on");
	else {
		for (let sel of checkedSelectors.value) {
			endpoint = env.net.urlParam(endpoint, "search("+sel.id+")", "on");
		}
	}
	return endpoint;
}

function queryServerWith(query:string, cursorPosition:number, capacity:number):void {
	const ep = buildRequestEndpoint(query, cursorPosition, capacity);
	env.net.fetch(ep)
		.then((response) => response.json())
		.then((data:SearchSuggestResponse) => {
			emptyResults();
			// Copy data
			for (let r of data.items) {
				results.value.push(r);
			}
			if (data.items.length > 0) selected.value = 0;	// first result
		});
}

function cursorPosition():number {
	let cursorPos = -1;
	if (queryInput.value) {
		const input = queryInput.value;
		let selectionStart = input.selectionStart;
		let selectionEnd = input.selectionEnd;
		if (selectionStart === selectionEnd) cursorPos = selectionStart;
	}
	return cursorPos;
}

const ROW_HEIGHT = 27;	/* Measured from the current CSS style */

function calcCapacity():number {
	if (queryInput.value) {
		const m = getElementMeasure(queryInput.value);
		const count = Math.floor( (m.spaceBottom - 4) / ROW_HEIGHT);
		return Math.min(Math.max(8, count), 50);
	}
	return 15;
}

function queryServer(q:string) {
	if (q == null) q = "";
	if (q.length < 2) emptyResults();
	else {
		// Determine cursor position
		let cursorPos = cursorPosition();
		let capacity = calcCapacity();
		queryServerWith(q, cursorPos, capacity);
	}
}

function loadDescriptors() {
	ClientCache.session("search").getOrLoad("descriptors", async () => {
		return env.net.fetch("api/search-descriptors").then((resp) => resp.json());
	}, 0).then((serverList:SearchDescriptor[]) => {
		const savedScopesStr = localStorage.getItem("_search_scope_");
		let savedScopes = [];
		if (savedScopesStr) {
			savedScopes = JSON.parse(savedScopesStr);
		}

		descriptors.value.length = 0;
		for (var serverItem of serverList) {
			const si:SelectableDescriptor = serverItem as SelectableDescriptor;
			si.selected = savedScopes.includes(si.id);
			descriptors.value.push(si);
		}
	});
}

function selectRow(idx: number):void {
	selected.value = idx;
}

function doAction(idx: number):void {
	const item = results.value[idx];
	const loc = item.url;
	if (loc && loc !== '#') {
		go(loc);
	}
}

function fullSearch():void {
	let q = query.value.trim();
	if (q.length < 2) return;

	go(buildFullSearchLink(q));
}

let blurTimeout;

function inputFocus(e) {
	if (blurTimeout) {
		clearTimeout(blurTimeout);
		blurTimeout = 0;
	}
	inFocus.value = true;
	opsChain.hide();
	queryServer(query.value);
}

function inputBlur(e) {
	blurTimeout = setTimeout(() => {
		inFocus.value = false;
	}, 200);
}

function keyDown(e: KeyboardEvent) {
	if (!resultsChain.visible.value) return;

	if (e.keyCode === 38 /* UP */) {
		if (selected.value <= 0) selectRow(rows.value - 1);
		else selectRow(selected.value - 1);
		e.preventDefault();
	} else if (e.keyCode === 40 /* DOWN */) {
		if (selected.value >= (rows.value - 1)) selectRow(0);
		else selectRow(selected.value + 1);
		e.preventDefault();
	} else if (e.keyCode === 13 /* ENTER */) {
		if (selected.value >= 0) {
			doAction(selected.value);
		}
		e.preventDefault();
	} else if (e.keyCode === 27 /* ESC */) {
		queryInput.value.blur();
	}
}

function toggleDescriptorDrawer(e:MouseEvent) {
	opsChain.toggle();
}

watch(() => opsChain.visible.value, (o) => {
	updateResultsVisibility();
	nextTick(() => {
		if (!o && queryInput.value) {
			queryInput.value.focus();
		}
	});
});

watch(() => query.value, (n, o) => {
	rememberQuery(n);
	queryServer(n);
});

watch(() => inFocus.value, updateResultsVisibility);
watch(() => rows.value, updateResultsVisibility);

onMounted(() => {
	loadDescriptors();
});
</script>

<style>
body {
	/* must be in the body to be visible for boats */
	--search-font-size: 16px;
	--search-height: 26px;
	--search-widget-radius: calc(var(--search-height) / 2);
}

.search-widget {
	font-size: var(--search-font-size);
	border-radius: var(--search-widget-radius);
	position: relative;
	background-color: var(--bg);
	color:var(--fg);
	display: inline-flex;
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
}
.search-widget-global {
	width: 400px;
}
.app-header-vertical .search-widget-global {
	width: 100%;
}

.search-widget .searchButton {
	height: var(--search-height);
	font-size: 14px;
	line-height: var(--search-height);
	padding: 0 var(--gap);
	margin: 0;
	border:0;
	border-radius: 0 var(--search-widget-radius) var(--search-widget-radius) 0;
	text-shadow: none;
	flex: 0 0;
}

.search-widget .searchButton:hover {
	background-color: var(--bg);
	color: var(--fg);
}

.search-widget .searchButton input {
	height: 18px;
	width:18px;
	vertical-align: top;
	border:0;
	margin: 3px;
	padding:0;
	border-image-width:0 0 0 0;
}

.search-input {
	flex: 1 1;
	position: relative;

	& > .empty-search {
		position: absolute;
		font-size: 14px;
		line-height: var(--search-height);
		right: 4px;
		top: 0;
		opacity: 0.5;
		cursor: default;
		color: var(--fg);
		transition: color 500ms;

		&:hover {
			opacity: 1;
		}
	}
}

input[type=text].searchBox,
input[type=text].searchBoxSmaller {
	box-sizing: border-box;
	/* min-width: 288px; */
	width: 100%;
	height: var(--search-height);
	background-color: var(--bg);
	color: var(--fg);
	border:none;
	outline: 0;
	font-size: var(--search-font-size);
	line-height: var(--search-height);
	padding: 0 6px;
	margin: 0;
	box-shadow: none;
	border-radius: 0;
	transition: background-color 500ms, color 500ms;
}

input[type=text].searchBox:focus,
input[type=text].searchBoxSmaller:focus {
	box-shadow: none;
}

input[type=text].searchBoxSmaller {
	margin: 0;
	min-width: 100px;
}

.search-widget.openOptions {
	border-radius: var(--search-widget-radius) var(--search-widget-radius) var(--search-widget-radius) 0;
}


.search-options {
	line-height:var(--search-height);
	padding-left:var(--gap-l);
	padding-right:var(--gap);
	color: var(--fg);
	font-size: var(--font-size);
	font-weight: normal;
	border-radius: 12px 0 0 12px;
	cursor: pointer;
	position: relative;
	z-index: calc(var(--search-drop-z-index) + 100);	/* must be above the drop-down shadow */
	flex: 0 0;
	display: inline-flex;
	justify-content: space-between;
	align-items: baseline;
	gap: var(--gap-xs);
}
.search-options:hover {
	background: var(--bg);
}

.openOptions .search-options {
	background: var(--bg);
	border-radius: var(--search-widget-radius) 0 0 0;
	box-shadow: none;
}

.search-options span {
	font-size: var(--font-size);
	line-height: var(--search-height);
	display: inline-block;
}
.search-options img {
	vertical-align: middle;
	width:var(--search-height);
	height:var(--search-height);
	float: right;
}


.search-options-box {
	padding: var(--gap) var(--gap-l);
	background-color: var(--bg);
	color: var(--fg);
	font-size: var(--font-size);
	line-height: var(--icn-size);
	border-radius: 0 0 var(--search-widget-radius) var(--search-widget-radius);
	position: absolute;
	top: var(--search-height);
	left:0;
	z-index:var(--search-drop-z-index);
	box-shadow: 1px 1px 3px 0 rgba(0,0,0,0.25);
	text-align: left;
}

.search-options-box input[type="checkbox"] {
	margin:0;
	padding:0;
	vertical-align: middle;
}

</style>
